import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import mergeWith from 'lodash/mergeWith';
import {
  defaultMetrics,
  excludedMetrics,
  excludeMetricCategories,
  supportedDimensionCategories,
} from '../../constants';
import { MetricTypes } from '@pulse/metrics';
import { allDimensionsListSelector } from '@src/store/common/dimensionList/selectors';
import { currentMetricsMetaSelector } from '@src/store/common/selectors';
import {
  dimensionsCategory,
  dimensionsCategoryOrder,
} from '@src/utils/dimension/dimensionCategories';
import useDimensionsCategories from '@src/components/dimension-panel-hz/hooks/useDimensionsCategories';
import useMetricCategories from '@src/hooks/metrics/useMetricCategories';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';
import { getDimensionData } from '@src/components/app-viewer-sessions/utils';
import { metricsStore } from '@src/globalStore/metricsStore';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

const EmptyArray: any[] = [];
const EmptySet = new Set();

const processCustomize = (obj, type) => {
  return {
    ...obj,
    metric: obj.key,
    type,
  };
};

const findItem = (key, customizeData) => {
  for (const category in customizeData) {
    if (Object.prototype.hasOwnProperty.call(customizeData, category)) {
      for (const item of customizeData[category]) {
        if (item.key === key) {
          return item;
        }
      }
    }
  }
  return null;
};

// ensure deleted metrics will be removed
const genValidatedCustomized = (customized, customizeData) => {
  const res = customized
    .map((key) => {
      if (findItem(key, customizeData)) {
        return key;
      }
    })
    .filter((key) => key);
  return res;
};

const useSessionCustomize = (allMetrics: Record<string, any>, allDimensions: any[]) => {
  const dimensionsCategories = useDimensionsCategories({
    allDimensions,
    selectedDimensionsList: EmptyArray,
    excludeCategories: EmptyArray,
    excludeDimensions: EmptyArray,
    hiddenEmptyCategory: false,
  });
  const supportedMetrics = useMemo(() => {
    const res = {};
    Object.entries(allMetrics ?? {}).map(([key, value]) => {
      if (
        value.active &&
        value.type === MetricTypes.appGeneralMetrics &&
        !excludedMetrics.includes(value.name)
      ) {
        res[key] = value;
      }
    });
    return res;
  }, [allMetrics]);

  const metricCategories = useMetricCategories({
    supportedMetrics,
    selectedMetricList: EmptyArray,
    lockedMetricsSet: EmptySet,
    excludeCategories: excludeMetricCategories,
    allMetrics,
    shrinkPercentileMetric: true,
  });

  const customizeData = useMemo(() => {
    const res: Record<string, Record<string, string | boolean>[]> = {};

    supportedDimensionCategories.forEach((c) => {
      const category = dimensionsCategory.APP[c] ?? c;
      const processed = (dimensionsCategories[c] ?? []).map((dimension) =>
        processCustomize(dimension, 'dimensions'),
      );
      res[category] = processed;
    });

    Object.entries(metricCategories).forEach(([key, value]) => {
      const valueRes = ((value as any[]) ?? []).map((metric) =>
        processCustomize(metric, 'metrics'),
      );
      if (valueRes.length > 0) {
        res[key] = valueRes;
      }
    });
    return res;
  }, [metricCategories, dimensionsCategories]);

  return customizeData;
};

const useFlowCustomize = (
  allMetrics: Record<string, any>,
  allDimensions: any[],
  flowMetadata?: AppFlowMetadata,
) => {
  const { metricsCategoryMeta } = metricsStore.getState();
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const limitation = useCurrentLimitation();
  const currentDimensionsCategoryOrder = dimensionsCategoryOrder[CURRENT_PRODUCT_MODE];
  const dimensionsListCategory = dimensionsCategory[CURRENT_PRODUCT_MODE];
  const availableMetrics = useMemo(() => {
    if (limitation?.context?.type === 'flow' && limitation?.context?.id) {
      const currentFlowMeta = metricsStore.getState()?.flowConfigMeta?.[limitation?.context?.id];
      const excludedErrorScopeMetrics = currentFlowMeta.metaDefinition.metrics.reduce(
        (acc, item) => {
          // Only process if metric meets all conditions
          if (
            item.scope !== 'error' &&
            item.scope !== 'service' &&
            item.identifier !== currentFlowMeta.metrics.avg_complete_duration &&
            !item.active &&
            item.category
          ) {
            const metricDetails = getMetricMetaByKey({
              metricKey: item.identifier,
              flowOverride: !!flowId,
              frustrationSignalOverride: !!frustrationSignalId,
            });

            if (metricDetails) {
              if (!acc[item.category]) {
                acc[item.category] = [];
              }
              const metricData = {
                key: metricDetails.name,
                metric: metricDetails.name,
                name: metricDetails.displayName,
                type: metricDetails.METRICS,
              };
              acc[item.category].push(metricData);
            }
          }
          return acc;
        },
        {} as any,
      );
      return excludedErrorScopeMetrics;
    }
  }, [flowMetadata, flowId, frustrationSignalId]);

  // Exclude dimensions with only service or error scope
  const excludeDimensions = useMemo(() => {
    if (!flowMetadata?.metaDefinition.dimensions) return undefined;

    const includeSet = new Set<string>();
    const excludeSet = new Set<string>();

    flowMetadata.metaDefinition.dimensions.forEach((item) => {
      const { scope, identifier } = item;

      if (scope === 'service' || scope === 'error') {
        excludeSet.add(identifier);
      } else {
        includeSet.add(identifier);
      }

      if (includeSet.has(identifier) && excludeSet.has(identifier)) {
        excludeSet.delete(identifier);
      }
    });
    return Array.from(excludeSet);
  }, [flowMetadata?.metaDefinition.dimensions]);
  const dimensionsCategories = useDimensionsCategories({
    allDimensions,
    selectedDimensionsList: EmptyArray,
    excludeCategories: EmptyArray,
    excludeDimensions: excludeDimensions ?? EmptyArray,
    hiddenEmptyCategory: false,
  });

  return useMemo(() => {
    const dimensionData = getDimensionData({
      dimensionsCategories,
      currentDimensionsCategoryOrder,
      dimensionsListCategory,
    });

    delete dimensionData.Mandatory;

    return mergeWith({}, dimensionData, availableMetrics, (a, b) =>
      Array.isArray(a) && Array.isArray(b) ? [...b, ...a] : undefined,
    );
  }, [
    availableMetrics,
    currentDimensionsCategoryOrder,
    dimensionsCategories,
    dimensionsListCategory,
    metricsCategoryMeta,
    flowMetadata,
  ]);
};

export const useCustomize = ({
  flowMetadata,
  savedMetrics,
}: {
  flowMetadata?: any;
  savedMetrics?: string[];
}) => {
  const [customized, setCustomized] = useState<string[]>(EmptyArray);
  const allDimensions = useSelector(allDimensionsListSelector) as any[];
  const allMetrics = useSelector(currentMetricsMetaSelector) as any[];
  const sessionCustomize = useSessionCustomize(allMetrics, allDimensions);
  const customizeData = sessionCustomize;

  useEffect(() => {
    // If we have saved metrics from localStorage, use those instead of defaults
    if (savedMetrics && savedMetrics.length > 0) {
      // Filter out metrics that are not available in customizeData
      // This excludes mandatory dimensions (client_id, app_user_id, device_name, etc.)
      const availableMetricsSet = new Set<string>();
      Object.values(customizeData).forEach((categoryItems: any) => {
        categoryItems.forEach((item: any) => {
          if (item.key) {
            availableMetricsSet.add(item.key);
          }
        });
      });

      const filteredSavedMetrics = savedMetrics.filter((metric) => availableMetricsSet.has(metric));

      setCustomized(filteredSavedMetrics.length > 0 ? filteredSavedMetrics : defaultMetrics);
    } else {
      setCustomized(defaultMetrics);
    }
  }, [customizeData, savedMetrics]);

  const onCustomizedChange = useCallback((value: string[] | string) => {
    let res = value as string[];
    if (value === 'default') {
      res = defaultMetrics;
    }
    setCustomized(res);
  }, []);

  const transformedCustomizeData = useMemo(() => {
    const { metricsCategoryMeta } = metricsStore.getState();

    if (!customizeData || typeof customizeData !== 'object') return customizeData;

    const result: Record<string, any[]> = {};

    Object.entries(customizeData).forEach(([key, value]) => {
      const displayName = metricsCategoryMeta?.[key]?.displayName as string;
      if (displayName) {
        result[displayName] = value as any[];
      } else {
        result[key] = value as any[];
      }
    });

    return result;
  }, [customizeData]);

  return {
    customized,
    customizeData,
    onCustomizedChange,
    transformedCustomizeData,
  };
};
